// Core Router - Protected Route Component
// Location: src/core/router/ProtectedRoute.tsx

import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "../store/authStore";
import { useProviderStore } from "../store/providerStore";
import { ROUTES, getDashboardRoute, getProviderLandingRoute } from "./routes";
import { resolveProviderRoute } from "./resolveProviderRoute";
import type { UserRole } from "../../types/auth.types";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  allowedRoles?: UserRole[];
  redirectTo?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  allowedRoles = [],
  redirectTo = ROUTES.LOGIN,
}) => {
  const { isAuthenticated, isLoading, user, activeView } = useAuthStore();
  const location = useLocation();

  if (isLoading) {
    return null;
  }

  if (requireAuth && !isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  if (allowedRoles.length > 0) {
    const effectiveRole = activeView ?? user?.role;
    if (!effectiveRole || !allowedRoles.includes(effectiveRole)) {
      return <Navigate to={ROUTES.UNAUTHORIZED} replace />;
    }
  }

  return <>{children}</>;
};

export const CustomerRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <ProtectedRoute allowedRoles={["customer"]}>{children}</ProtectedRoute>;

export const ProviderRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <ProtectedRoute allowedRoles={["provider"]}>
    <ProviderOnboardingGate>{children}</ProviderOnboardingGate>
  </ProtectedRoute>
);

export const AdminRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <ProtectedRoute allowedRoles={["admin"]}>{children}</ProtectedRoute>;

const ONBOARDING_PREFIX = "/onboarding";

const isOnboardingPath = (pathname: string): boolean =>
  pathname === ROUTES.ONBOARDING.SETUP ||
  pathname === ROUTES.ONBOARDING.LICENCE ||
  pathname.startsWith(ONBOARDING_PREFIX + "/vehicle");

/**
 * Redirects providers to the correct onboarding step unless already on an onboarding route.
 */
export const ProviderOnboardingGate: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const location = useLocation();
  const authLoading = useAuthStore((s) => s.isLoading);
  const refreshVehicles = useProviderStore((s) => s.refreshVehicles);
  const [checking, setChecking] = useState(true);
  const [target, setTarget] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;

    let cancelled = false;
    const run = async () => {
      setChecking(true);
      const state = useProviderStore.getState();
      let list = state.vehicles;
      // Always await fetch when empty (including in-flight refresh from auth hydrate).
      if (list.length === 0) {
        list = await refreshVehicles();
      }
      const resolved = resolveProviderRoute("provider", list);
      if (!cancelled) {
        setTarget(resolved);
        setChecking(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, authLoading, refreshVehicles]);

  if (authLoading || checking) return null;

  if (isOnboardingPath(location.pathname)) {
    if (
      target === ROUTES.PROVIDER.DASHBOARD &&
      location.pathname === ROUTES.ONBOARDING.SETUP
    ) {
      return <Navigate to={ROUTES.PROVIDER.DASHBOARD} replace />;
    }
    return <>{children}</>;
  }

  const providerPaths = [
    ROUTES.PROVIDER.DASHBOARD,
    ROUTES.PROVIDER.ACTIVE_DELIVERIES,
  ];

  const onProviderApp =
    providerPaths.some((p) => location.pathname.startsWith(p)) ||
    location.pathname.startsWith("/provider");

  // Only block when org/vehicle setup is incomplete — not for verify/pending review.
  if (onProviderApp && target === ROUTES.ONBOARDING.SETUP && location.pathname !== target) {
    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
};

export const GuestRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, isLoading, user } = useAuthStore();
  const location = useLocation();
  const [redirect, setRedirect] = useState<string | null>(null);
  const [redirectResolved, setRedirectResolved] = useState(false);

  useEffect(() => {
    setRedirectResolved(false);
    if (!isAuthenticated || !user) {
      setRedirect(null);
      setRedirectResolved(true);
      return;
    }
    const from = (location.state as { from?: { pathname?: string } })?.from
      ?.pathname;
    // Don't bounce back into the onboarding flow — let the user access the guest page
    if (from && !isOnboardingPath(from)) {
      setRedirect(from);
      setRedirectResolved(true);
      return;
    }
    if (user.role === "provider") {
      getProviderLandingRoute().then((route) => {
        // Providers whose only destination is onboarding can still reach guest pages
        // (e.g. to log in with a different account or abandon onboarding)
        setRedirect(isOnboardingPath(route) ? null : route);
        setRedirectResolved(true);
      });
    } else {
      setRedirect(getDashboardRoute(user.role));
      setRedirectResolved(true);
    }
  }, [isAuthenticated, user, location.state]);

  if (isLoading) return null;

  if (isAuthenticated && redirectResolved && redirect) {
    return <Navigate to={redirect} replace />;
  }

  // Still resolving destination — render nothing to avoid flash
  if (isAuthenticated && !redirectResolved) return null;

  return <>{children}</>;
};
